import { isDefined, isTruthy } from 'notable-common';
import { z } from 'zod';
import { FlowConversationBlockMode, FlowConversationTagEditMode } from './enums.js';
import { FlowDurationUnits } from '../../../duration/duration.js';
import { FlowVersionTarget } from '../../../flow/versionTarget.js';
import { FlowProducedType } from '../../../flowRun/producedType.js';
import { FlowBlockValueFieldSchema, FlowBlockValueFieldSchemaVariant, } from '../../blockValue/fieldSchema.js';
import { FlowBlockValueTemplateDurationSchema } from '../../blockValue/templateDuration.js';
import { BaseFlowBlockSchema, FlowBlockReturnTypeSchema } from '../block.js';
import { FlowArgumentSchema } from '../subflow/shared/flowArgument.js';
import { FlowBlockType } from '../type.js';
const TagsArraySchema = z
    .array(z.string().min(1))
    .refine((tags) => new Set(tags).size === tags.length, {
    message: 'All items must be unique, no duplicate values allowed',
});
export const FLOW_CONVERSATION_MAXIMUM_TIMEOUT_DURATION = {
    value: 30,
    units: FlowDurationUnits.Days,
};
const TagsExpressionSchema = FlowBlockValueFieldSchema({
    variant: FlowBlockValueFieldSchemaVariant.OptionalTemplate,
});
const TagsConfigSchema = z.union([TagsArraySchema, TagsExpressionSchema]);
export const FLOW_CONVERSATION_MAXIMUM_NO_PATIENT_RESPONSE_TIMEOUT_DURATION = {
    ...FLOW_CONVERSATION_MAXIMUM_TIMEOUT_DURATION,
};
export const FlowConversationCreateConfigSchema = z.object({
    agentPhoneNumber: z.string().optional(),
    callerPhoneNumberId: z.uuid('Must be a valid phone number configuration id.').nullish(),
    firstMessage: z.string().min(1),
    tags: TagsConfigSchema,
    timeout: FlowBlockValueTemplateDurationSchema({
        pathName: 'timeout',
        maxDuration: FLOW_CONVERSATION_MAXIMUM_TIMEOUT_DURATION,
    }),
    noPatientResponseTimeout: FlowBlockValueTemplateDurationSchema({
        pathName: 'noPatientResponseTimeout',
        maxDuration: FLOW_CONVERSATION_MAXIMUM_NO_PATIENT_RESPONSE_TIMEOUT_DURATION,
    }).optional(),
    priority: z.string().optional(),
    patientReplyHandler: z
        .object({
        flowDefinitionId: z.string(),
        versionTarget: z.enum(FlowVersionTarget),
        flowId: z.string(),
        metadata: z.array(FlowArgumentSchema).optional(),
    })
        .optional(),
    mode: z.literal(FlowConversationBlockMode.Create),
    assignmentGroupId: z.string().optional(),
});
export const FlowConversationReopenConfigSchema = z.object({
    existingConversationId: z.string().min(1),
    firstMessage: z.string().optional(),
    timeout: FlowBlockValueTemplateDurationSchema({
        pathName: 'timeout',
        maxDuration: FLOW_CONVERSATION_MAXIMUM_TIMEOUT_DURATION,
    }),
    noPatientResponseTimeout: FlowBlockValueTemplateDurationSchema({
        pathName: 'noPatientResponseTimeout',
        maxDuration: FLOW_CONVERSATION_MAXIMUM_NO_PATIENT_RESPONSE_TIMEOUT_DURATION,
    }).optional(),
    mode: z.literal(FlowConversationBlockMode.Reopen),
});
export const FlowConversationReplyConfigSchema = z.object({
    existingConversationId: z.string().min(1),
    firstMessage: z.string().min(1),
    mode: z.literal(FlowConversationBlockMode.Reply),
});
export const FlowConversationCloseConfigSchema = z.object({
    existingConversationId: z.string().min(1),
    firstMessage: z.string().optional(),
    mode: z.literal(FlowConversationBlockMode.Close),
    resolutionReason: z.string().optional(),
    resolutionDetails: z.record(z.string(), z.unknown()).optional(),
});
export const FlowConversationUpdateConfigSchema = z.object({
    existingConversationId: z.string().min(1),
    mode: z.literal(FlowConversationBlockMode.Update),
    tagEditMode: z.enum(FlowConversationTagEditMode),
    tagsToAdd: z.array(z.string().min(1)).optional().nullable(),
    tagsToRemove: z.array(z.string().min(1)).optional().nullable(),
    tags: TagsConfigSchema.optional().nullable(),
    assignmentGroupId: z.string().optional(),
});
/**
 * @deprecated use mode create, reopen, reply or close
 */
export const FlowBaseConversationBlockConfigSchemaDeprecated = z.object({
    agentPhoneNumber: z.string().optional(),
    callerPhoneNumberId: z.uuid('Must be a valid phone number configuration id.').nullish(),
    firstMessage: z.string(),
    tags: TagsArraySchema,
    timeout: FlowBlockValueTemplateDurationSchema({
        pathName: 'timeout',
        maxDuration: FLOW_CONVERSATION_MAXIMUM_TIMEOUT_DURATION,
    }),
    priority: z.string().optional(),
    existingConversationId: z.string().optional(),
    patientReplyHandler: z
        .object({
        flowDefinitionId: z.string(),
        versionTarget: z.enum(FlowVersionTarget),
        flowId: z.string(),
        metadata: z.array(FlowArgumentSchema).optional(),
    })
        .optional(),
});
export const FlowConversationWaitUntilClosedConfigSchema = 
// eslint-disable-next-line @typescript-eslint/no-deprecated -- the deprecated modes are exactly what this schema describes
FlowBaseConversationBlockConfigSchemaDeprecated.extend({
    mode: z
        .literal(FlowConversationBlockMode.WaitUntilClosed)
        .optional()
        .default(FlowConversationBlockMode.WaitUntilClosed),
});
export const FlowConversationNonBlockingConfigSchema = 
// eslint-disable-next-line @typescript-eslint/no-deprecated -- the deprecated modes are exactly what this schema describes
FlowBaseConversationBlockConfigSchemaDeprecated.extend({
    mode: z.literal(FlowConversationBlockMode.NonBlocking),
});
const ConversationBlockConfigSchema = z
    .discriminatedUnion('mode', [
    FlowConversationCreateConfigSchema,
    FlowConversationReopenConfigSchema,
    FlowConversationReplyConfigSchema,
    FlowConversationCloseConfigSchema,
    FlowConversationUpdateConfigSchema,
    FlowConversationWaitUntilClosedConfigSchema,
    FlowConversationNonBlockingConfigSchema,
])
    .refine((config) => {
    if (config.mode === FlowConversationBlockMode.Create ||
        config.mode === FlowConversationBlockMode.WaitUntilClosed ||
        config.mode === FlowConversationBlockMode.NonBlocking) {
        return isDefined(config.callerPhoneNumberId) || isTruthy(config.agentPhoneNumber);
    }
    return true;
}, {
    message: 'Either callerPhoneNumberId or agentPhoneNumber must be provided',
    path: ['agentPhoneNumber'],
});
export const FlowConversationBlockSchema = BaseFlowBlockSchema.extend({
    type: z.literal(FlowBlockType.Conversation),
    config: ConversationBlockConfigSchema,
    returnType: FlowBlockReturnTypeSchema(FlowProducedType.AgentConversation),
});
export const FlowConversationBlockSchemaDeprecated = BaseFlowBlockSchema.extend({
    type: z.literal(FlowBlockType.Conversation),
    config: z.union([
        FlowConversationWaitUntilClosedConfigSchema,
        FlowConversationNonBlockingConfigSchema,
    ]),
    returnType: FlowBlockReturnTypeSchema(FlowProducedType.AgentConversation),
});
//# sourceMappingURL=config.js.map